import { useEffect, useState } from 'react';
import { Link, useOutletContext } from 'react-router';
import { ArrowDown, ArrowRight, ArrowUp, GripVertical } from 'lucide-react';
import { TEAM_PRIORITY_FILTERS, type Session, type TeamPriorityFilter, type TeamPriorityItem } from '@product-portal/shared';
import { Button, buttonVariants } from '@/components/ui/button';
import { ImpactTag } from '@/components/portal/impact-tag';
import { KpiCard } from '@/components/portal/kpi-card';
import { PageTitle } from '@/components/portal/page-title';
import { OrderBadge, SectionCard } from '@/components/portal/section-card';
import { SelectField } from '@/components/portal/select-field';
import { StatusPill } from '@/components/portal/status-pill';
import { TypeTag } from '@/components/portal/type-tag';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useRowDrag } from '@/features/prioritization/reorder';
import { useSaveTeamPriorities, useTeamPriorities } from '@/hooks/use-product';
import { formatDate, shortName } from '@/lib/format';
import { formatBusinessDays, NO_PRODUCT_LABEL, TEAM_PRIORITY_FILTER_LABELS } from '@/lib/labels';
import { cn } from '@/lib/utils';

/**
 * 06 · Priorisation par équipe. One screen, two uses — enforced by the API:
 * - PO: every team, the DRAFT order it prepares, reordering and « Envoyer ma priorisation »;
 * - Référent: its own team, the PUBLISHED order as pills, impact as tags, no button at all.
 */
export function TeamPrioritiesPage() {
  const session = useOutletContext<Session>();
  const [teamId, setTeamId] = useState<string | null>(null);
  const [filter, setFilter] = useState<TeamPriorityFilter>('to_prioritize');
  const [productId, setProductId] = useState('');
  const query = useTeamPriorities({ teamId, filter, productId });
  const data = query.data;
  const save = useSaveTeamPriorities(data?.team.id ?? '');
  const [rows, setRows] = useState<TeamPriorityItem[]>([]);
  const serverOrder = JSON.stringify(data?.items.map((i) => i.id) ?? []);
  useEffect(() => setRows(data?.items ?? []), [serverOrder, data?.items]);
  const drag = useRowDrag(rows, setRows);
  // Reordering makes sense on the ordered rows only, all products together.
  const canReorder = data?.canEdit === true && filter === 'to_prioritize' && !productId;
  const changed = JSON.stringify(rows.map((r) => r.id)) !== serverOrder;

  if (query.isError) return <p className="m-0 text-sm text-muted-foreground">Cette vue n'est pas accessible.</p>;

  return (
    <>
      <PageTitle
        title={data?.team.name ?? 'Mon équipe'}
        subtitle={
          data?.canEdit
            ? "Vue PO · priorisation de l'équipe. Le référent de l'équipe voit cette page en lecture seule, avec l'ordre publié."
            : "Ordre et impact fixés par l'équipe produit pour les demandes de votre équipe, en lecture seule."
        }
      >
        {data && data.teams.length > 1 && (
          <SelectField label="Équipe" value={data.team.id} onChange={(e) => setTeamId(e.target.value)} className="min-w-[200px] text-[13px]">
            {data.teams.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </SelectField>
        )}
        {data?.canEdit && (
          <>
            <a href={`/api/product/team-priorities/${data.team.id}/export.csv`} className={cn(buttonVariants({ variant: 'secondary' }), 'text-[13px] text-foreground no-underline hover:text-foreground')}>
              Exporter
            </a>
            <Button type="button" className="text-[13px]" disabled={!canReorder || !changed || save.isPending} onClick={() => save.mutate(rows.map((r) => r.id))}>
              Envoyer ma priorisation
            </Button>
          </>
        )}
      </PageTitle>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiCard label="Nouvelles demandes" value={data?.kpis.newCount ?? '—'} />
        <KpiCard label="Qualifiées" value={data?.kpis.qualifiedCount ?? '—'} tone="info" />
        <KpiCard label="Délai médian de qualification" value={data?.kpis.medianDelay != null ? formatBusinessDays(data.kpis.medianDelay) : '—'} />
        <KpiCard label="Livrées sur 90 jours" value={data?.kpis.deliveredLast90Days ?? '—'} tone="success" />
      </div>

      <SectionCard
        title="Demandes de l'équipe"
        toolbar={
          <>
            <div role="group" aria-label="Filtre" className="flex flex-wrap gap-1.5">
              {TEAM_PRIORITY_FILTERS.map((f) => (
                <button
                  key={f}
                  type="button"
                  aria-pressed={filter === f}
                  onClick={() => setFilter(f)}
                  className={cn(
                    'min-h-8 cursor-pointer rounded-control border px-3 font-[inherit] text-[13px]',
                    filter === f ? 'border-primary bg-accent font-bold text-accent-strong' : 'border-border bg-card text-foreground-secondary',
                  )}
                >
                  {TEAM_PRIORITY_FILTER_LABELS[f]} ({data?.counts[f] ?? 0})
                </button>
              ))}
            </div>
            <div className="ml-auto">
              <SelectField label="Produit" size="sm" value={productId} onChange={(e) => setProductId(e.target.value)}>
                <option value="">Tous</option>
                {(data?.products ?? []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </SelectField>
            </div>
          </>
        }
        footer={
          <div className="border-t border-border px-5 py-3 text-[13px] text-muted-foreground">
            Les bugs sont pris en charge directement par l'équipe produit et n'apparaissent pas dans l'arbitrage. L'ordre et l'impact sont fixés par l'équipe
            produit ; pour les faire évoluer, échangez avec le PO.
            {canReorder && ' Glissez une ligne pour la déplacer : seul l’ordre brouillon change, l’ordre publié bouge à « Publier le backlog ».'}
          </div>
        }
      >
        <Table minWidth={1000}>
          <TableHeader>
            <TableRow>
              <TableHead className="w-14">Ordre</TableHead>
              <TableHead>Demande</TableHead>
              <TableHead>Demandeur</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Impact équipe</TableHead>
              {/* Not in mock-up 06: where the request stands (Robin Meyssonnier, 2026-10-21). */}
              <TableHead>Statut</TableHead>
              <TableHead>Soumise le</TableHead>
              <TableHead>
                <span className="sr-only">Action</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((item, index) => (
              <TableRow
                key={item.id}
                {...(canReorder ? drag.rowProps(item.id) : {})}
                className={cn(canReorder && 'cursor-grab', drag.over === item.id && drag.dragged !== item.id && 'bg-accent', drag.dragged === item.id && 'opacity-50')}
              >
                <TableCell>
                  <span className="flex items-center gap-1">
                    {canReorder && <GripVertical aria-hidden="true" className="size-4 text-muted-foreground" />}
                    <OrderBadge order={canReorder ? index + 1 : item.order} />
                    {canReorder && (
                      <span className="flex flex-col">
                        <button type="button" aria-label={`Monter ${item.reference}`} disabled={index === 0} onClick={() => drag.step(item.id, -1)} className="cursor-pointer border-0 bg-transparent p-0 text-muted-foreground disabled:opacity-30">
                          <ArrowUp className="size-3.5" />
                        </button>
                        <button type="button" aria-label={`Descendre ${item.reference}`} disabled={index === rows.length - 1} onClick={() => drag.step(item.id, 1)} className="cursor-pointer border-0 bg-transparent p-0 text-muted-foreground disabled:opacity-30">
                          <ArrowDown className="size-3.5" />
                        </button>
                      </span>
                    )}
                  </span>
                </TableCell>
                <TableCell>
                  <Link to={`/demandes/${item.id}`} className="font-bold text-foreground no-underline hover:text-foreground">
                    {item.title}
                  </Link>
                  <span className="mt-0.5 block text-xs text-muted-foreground">
                    {item.reference} · {item.product?.name ?? NO_PRODUCT_LABEL}
                  </span>
                </TableCell>
                <TableCell className="text-foreground-secondary">{shortName(item.requester)}</TableCell>
                <TableCell>
                  <TypeTag type={item.type} />
                </TableCell>
                <TableCell>
                  <ImpactTag impact={item.impact} />
                </TableCell>
                <TableCell>
                  <StatusPill status={item.status} />
                </TableCell>
                <TableCell className="whitespace-nowrap text-foreground-secondary">{formatDate(item.submittedAt)}</TableCell>
                <TableCell>
                  <Link
                    to={session.role === 'po' ? `/produit/demandes/${item.id}` : `/demandes/${item.id}`}
                    className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'min-h-9 no-underline')}
                  >
                    Détail
                    <ArrowRight aria-hidden="true" className="size-4" strokeWidth={2.2} />
                  </Link>
                </TableCell>
              </TableRow>
            ))}
            {data && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-muted-foreground">
                  Aucune demande dans ce filtre.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </SectionCard>

      {data && (
        <section className="flex flex-col gap-2.5 rounded-card border border-border bg-card px-5 py-4">
          <h2 className="m-0 text-[15px] font-bold">Membres de l'équipe sur le portail</h2>
          {/* Mock-up 06 says « via le groupe »: there is no group sync, the PO attaches people by hand. */}
          <p className="m-0 text-[13px] text-foreground-secondary">
            {data.memberCount} personne{data.memberCount > 1 ? 's' : ''} rattachée{data.memberCount > 1 ? 's' : ''} à l'équipe par l'équipe produit.
          </p>
        </section>
      )}
    </>
  );
}
